import { lazy } from "react";
import { Navigate, RouteObject } from "react-router-dom";

// Components
import MainLayout from "../components/layout/MainLayout";
import RouteProtector from "../components/routeProtector/RouteProtector";
import PageNotFound from "../components/pageNotFound/pageNotFound";
import VerifyUser from "../components/authManagement/verifyUser/verifyUser";
import AccessDenied from "../components/accessDenied/AccessDenied";
import DeleteAccount from "../components/authManagement/deleteAccount/DeleteAccount";
import FarmerDetails from "../components/farmersData/farmersManagement/farmerDetails/FarmerDetails";
import AddUpdateFarmer from "../components/farmersData/farmersManagement/addUpdateFarmer/addUpdateFarmer/AddUpdateFarmer";
import FieldDetails from "../components/farmersData/fieldsManagement/fieldDetails/FieldDetails";
import YieldConfig from "../components/appSettings/yieldManagement/YieldConfig";

// Types
import { EUserRoles } from "../components/shared/commonTypes/commonTypes";
import { FormTypeProvider } from "../components/masterData/FormBuilder/common/context/FormTypeContext";
const FarmersDataManager = lazy(
  () =>
    import(
      "../components/farmersData/farmersDataManagement/FarmersDataManagement"
    ),
);
const AuthManager = lazy(
  () => import("../components/authManagement/authManager/AuthManager"),
);
const Dashboard = lazy(() => import("../components/dashboard/Dashboard"));
// const FarmDataManager = lazy(
//   () => import("../components/farmManagement/farmDataManager/FarmDataManager"),
// );
const MasterDataManager = lazy(
  () => import("../components/masterData/masterDataManager/MasterDataManager"),
);
const UserManager = lazy(
  () => import("../components/userManagement/UserManager/UserManager"),
);
const FormBuilder = lazy(
  () => import("../components/masterData/FormBuilder/formBuilder/FormBuilder"),
);

const AddField = lazy(
  () =>
    import(
      "../components/farmersData/fieldsManagement/addUpdateField/addUpdateField/AddUpdateField"
    ),
);

const AddUpdateDealer = lazy(
  () =>
    import(
      "../components/farmersData/dealersManagement/addUpdateDealer/addUpdateDealer/AddUpdateDealer"
    ),
);

const DealerExport = lazy(
  () =>
    import(
      "../components/farmersData/dealersManagement/dealerExport/DealerExport"
    ),
);

const DealerDetails = lazy(
  () =>
    import(
      "../components/farmersData/dealersManagement/dealerDetails/DealerDetails"
    ),
);

const FormBuilderManager = lazy(
  () =>
    import(
      "../components/masterData/formBuilderManager/formBuilderManager/formBuilderManager"
    ),
);

const DashboardManager = lazy(
  () =>
    import(
      "../components/masterData/dashboardManager/dashboardManager/DashboardManager"
    ),
);

const PendingRequestManager = lazy(
  () =>
    import(
      "../components/masterData/pendingRequestManager/pendingRequestManager/PendingRequestManager"
    ),
);

const PendingFieldDetails = lazy(
  () =>
    import(
      "../components/masterData/pendingRequestManager/fieldDetails/PendingFieldDetails"
    ),
);

const CropCycleDetails = lazy(
  () =>
    import(
      "../components/farmersData/cropCyclesManagement/cropCycleDetails/CropCycleDetails"
    ),
);

const ManageCropCycle = lazy(
  () =>
    import(
      "../components/farmersData/cropCyclesManagement/addUpdateCropCycle/AddUpdateCropCycle"
    ),
);

const ActivityDetails = lazy(
  () =>
    import(
      "../components/farmersData/cropCyclesManagement/activityDetails/ActivityDetails"
    ),
);

const PublicFarmerReport = lazy(
  () => import("../components/publicFarmerReport/PublicFarmerReport"),
);

// Application routes
const routes: RouteObject[] = [
  { path: "/", element: <Navigate to="/verifyuser" replace /> },
  { path: "/authenticate", element: <AuthManager /> },
  { path: "/verifyuser", element: <VerifyUser /> },
  { path: "/deleteaccount", element: <DeleteAccount /> },
  { path: "/farmer-report/:farmerId", element: <PublicFarmerReport /> },
  {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        path: "/users",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin, EUserRoles.manager]}>
            <UserManager />
          </RouteProtector>
        ),
      },
      {
        path: "/farmers",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <FarmersDataManager />
          </RouteProtector>
        ),
      },
      {
        path: "/managefarmers",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <AddUpdateFarmer />
          </RouteProtector>
        ),
      },
      {
        path: "/managedealers",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin]}>
            <AddUpdateDealer />
          </RouteProtector>
        ),
      },
      {
        path: "/dealer-export",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin]}>
            <DealerExport />
          </RouteProtector>
        ),
      },
      {
        path: "/dealerdetails",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin]}>
            <DealerDetails />
          </RouteProtector>
        ),
      },
      {
        path: "/formbuilder",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin, EUserRoles.manager]}>
            <FormTypeProvider>
              <FormBuilder />
            </FormTypeProvider>
          </RouteProtector>
        ),
      },
      {
        path: "/addField",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <FormTypeProvider>
              <AddField />
            </FormTypeProvider>
          </RouteProtector>
        ),
      },
      {
        path: "/masters",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.superAdmin]}>
            <MasterDataManager />
          </RouteProtector>
        ),
      },
      {
        path: "/formbuilders",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin]}>
            <FormBuilderManager />
          </RouteProtector>
        ),
      },
      {
        path: "/dashboard",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.superAdmin]}>
            <Dashboard />
          </RouteProtector>
        ),
      },
      {
        path: "/admindashboard",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.agent,
              EUserRoles.manager,
            ]}
          >
            <DashboardManager />
          </RouteProtector>
        ),
      },
      {
        path: "/pendingrequest",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin, EUserRoles.manager]}>
            <PendingRequestManager />
          </RouteProtector>
        ),
      },
      {
        path: "/pendingfielddetails",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin, EUserRoles.manager]}>
            <PendingFieldDetails />
          </RouteProtector>
        ),
      },
      {
        path: "/FarmerDetails",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <FarmerDetails />
          </RouteProtector>
        ),
      },
      {
        path: "/FieldDetails",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <FieldDetails />
          </RouteProtector>
        ),
      },
      {
        path: "/cropcycledetails",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <CropCycleDetails />
          </RouteProtector>
        ),
      },
      {
        path: "/activitydetails",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <ActivityDetails />
          </RouteProtector>
        ),
      },
      {
        path: "/managecropcycle",
        element: (
          <RouteProtector
            allowedRoles={[
              EUserRoles.admin,
              EUserRoles.manager,
              EUserRoles.agent,
            ]}
          >
            <ManageCropCycle />
          </RouteProtector>
        ),
      },
      {
        path: "/yieldconfig",
        element: (
          <RouteProtector allowedRoles={[EUserRoles.admin]}>
            <YieldConfig />
          </RouteProtector>
        ),
      },
    ],
  },
  { path: "/accessdenied", element: <AccessDenied /> },
  { path: "*", element: <PageNotFound /> },
];

export default routes;
